import { useCallback, useEffect, useState } from 'react';
import { api, RequestFailed } from '../api/client';
import type { Invite } from '../api/types';
import { PasswordResetPanel } from './PasswordResetPanel';

interface Props {
  onClose: () => void;
}

type Status = 'active' | 'used' | 'expired';

function statusOf(invite: Invite): Status {
  if (invite.used) return 'used';
  return new Date(invite.expiresAt) < new Date() ? 'expired' : 'active';
}

const STATUS_LABEL: Record<Status, string> = {
  active: 'активна',
  used: 'использована',
  expired: 'истекла',
};

export function InvitePanel({ onClose }: Props) {
  const [note, setNote] = useState('');
  const [days, setDays] = useState(7);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [fresh, setFresh] = useState<Invite | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      setInvites(await api.invites());
    } catch (e) {
      setError(e instanceof RequestFailed ? e.error.message : 'Не удалось загрузить приглашения');
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  async function create(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const invite = await api.createInvite({ note: note.trim() || undefined, expiresInDays: days });
      setFresh(invite);
      setNote('');
      await copy(invite.url);
      await reload();
    } catch (e) {
      setError(e instanceof RequestFailed ? e.error.message : 'Не удалось создать приглашение');
    } finally {
      setBusy(false);
    }
  }

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(url);
      window.setTimeout(() => setCopied(null), 2500);
    } catch {
      // Буфер обмена может быть недоступен (нет разрешения, старый браузер) —
      // ссылка всё равно видна на экране и её можно выделить руками
      setCopied(null);
    }
  }

  async function revoke(invite: Invite) {
    if (!confirm(`Отозвать приглашение${invite.note ? ` «${invite.note}»` : ''}? Ссылка перестанет работать.`)) {
      return;
    }
    setError(null);
    try {
      await api.revokeInvite(invite.id);
      if (fresh?.id === invite.id) setFresh(null);
      await reload();
    } catch (e) {
      setError(e instanceof RequestFailed ? e.error.message : 'Не удалось отозвать');
    }
  }

  return (
    <div className="panel">
      <form onSubmit={create}>
        <div className="field">
          <label htmlFor="inviteNote">Кому приглашение</label>
          <input
            id="inviteNote"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Например, мама"
            maxLength={128}
            autoFocus
          />
        </div>

        <div className="field">
          <label htmlFor="inviteDays">Срок действия</label>
          <select id="inviteDays" value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={1}>1 день</option>
            <option value={7}>7 дней</option>
            <option value={30}>30 дней</option>
          </select>
        </div>

        <div className="panel-actions">
          <button type="submit" disabled={busy}>
            {busy ? 'Создаём…' : 'Создать ссылку'}
          </button>
          <button type="button" className="ghost" onClick={onClose}>Закрыть</button>
        </div>
      </form>

      {error && <p className="error">{error}</p>}

      {fresh && (
        <div className="invite-fresh">
          <p className="panel-hint" style={{ marginBottom: 6 }}>
            {copied === fresh.url
              ? 'Ссылка скопирована — отправьте её человеку любым способом'
              : 'Ссылка готова. Отправьте её человеку любым способом'}
          </p>
          <code className="invite-link">{fresh.url}</code>
          <button type="button" className="ghost" onClick={() => void copy(fresh.url)}>
            {copied === fresh.url ? 'скопировано ✓' : 'скопировать'}
          </button>
        </div>
      )}

      {invites.length > 0 && (
        <div className="panel-search">
          <div className="section-title" style={{ padding: '10px 0 2px' }}>Выданные приглашения</div>
          {invites.map((invite) => {
            const status = statusOf(invite);
            return (
              <div className="invite-row" key={invite.id}>
                <div className="invite-row-main">
                  <span className="name">{invite.note || 'без пометки'}</span>
                  <span className={'invite-status ' + status}>
                    {STATUS_LABEL[status]}
                    {invite.role === 'ADMIN' && ' · админ'}
                  </span>
                </div>
                {status === 'active' && (
                  <div className="invite-row-actions">
                    <button type="button" className="ghost" onClick={() => void copy(invite.url)}>
                      {copied === invite.url ? '✓' : 'копировать'}
                    </button>
                    <button type="button" className="ghost danger" onClick={() => void revoke(invite)}>
                      отозвать
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <PasswordResetPanel />
    </div>
  );
}
